import { forwardRef, useEffect, useImperativeHandle, useRef } from 'react'

interface TurnstileApi {
    render: (element: HTMLElement, options: Record<string, unknown>) => string
    reset: (widgetId?: string) => void
    remove: (widgetId: string) => void
}

declare global {
    interface Window {
        turnstile?: TurnstileApi
    }
}

const SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit'

let scriptPromise: Promise<void> | null = null

const loadScript = (): Promise<void> => {
    if (window.turnstile) return Promise.resolve()

    if (!scriptPromise) {
        scriptPromise = new Promise((resolve, reject) => {
            const script = document.createElement('script')

            script.src = SCRIPT_SRC
            script.async = true
            script.defer = true
            script.onload = () => resolve()
            script.onerror = () => {
                scriptPromise = null
                reject(new Error('Turnstile could not be loaded.'))
            }

            document.head.appendChild(script)
        })
    }

    return scriptPromise
}

export interface TurnstileHandle {
    reset: () => void
}

interface TurnstileProps {
    siteKey: string | null
    action: string
    locale: string
    onToken: (token: string | null) => void
}

// Cloudflare Turnstile in "interaction only" mode: invisible for normal visitors,
// a short challenge only appears when Cloudflare sees suspicious activity.
export const Turnstile = forwardRef<TurnstileHandle, TurnstileProps>(({ siteKey, action, locale, onToken }, ref) => {
    const container = useRef<HTMLDivElement>(null)
    const widget = useRef<string | null>(null)
    const callback = useRef(onToken)

    useEffect(() => {
        callback.current = onToken
    }, [onToken])

    useImperativeHandle(ref, () => ({
        reset: () => {
            if (widget.current && window.turnstile) {
                callback.current(null)
                window.turnstile.reset(widget.current)
            }
        }
    }), [])

    useEffect(() => {
        if (!siteKey || !container.current) return

        let cancelled = false

        loadScript()
            .then(() => {
                if (cancelled || !container.current || !window.turnstile) return

                widget.current = window.turnstile.render(container.current, {
                    sitekey: siteKey,
                    action,
                    language: locale,
                    theme: 'dark',
                    appearance: 'interaction-only',
                    callback: (token: string) => callback.current(token),
                    'expired-callback': () => callback.current(null),
                    'error-callback': () => callback.current(null)
                })
            })
            .catch(() => callback.current(null))

        return () => {
            cancelled = true

            if (widget.current && window.turnstile) window.turnstile.remove(widget.current)

            widget.current = null
        }
    }, [siteKey, action, locale])

    if (!siteKey) return null

    return <div ref={container} className='flex justify-center' />
})

Turnstile.displayName = 'Turnstile'
